import { vendorAwardsAtom } from '@client/jotai/storefront/awards-summary';
import { ReadMore } from '@components/read-more/ReadMore';
import { useDetailedPricing } from '@hooks/use-detailed-pricing';
import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { useTrackStorefrontImpression } from '@hooks/useTrackStorefrontImpression';
import { GridRow } from '@tkww/orion-web-ds-structure';
import { GridColumn } from '@tkww/orion-web-ds-structure';
import { TextBody } from '@tkww/orion-web-ds-text';
import { compose } from '@xo-union/react-css-modules';
import { useAtomValue } from 'jotai';
import map from 'lodash/map';
import React, { type FC, Fragment, useState } from 'react';
import type { Decorated } from 'types/vendor';
import DetailsHelper from '../../helpers';
import { Awards } from '../awards/Awards';
import { Designers } from '../designers/Designers';
import Styles from './styles.scss';

interface DetailItemProps {
	category: string;
	details: string[];
}
const DetailItem = ({ category, details }: DetailItemProps) => {
	const isLiteStorefront = useLiteStorefront();
	return !isLiteStorefront ? (
		<DetailItemFull category={category} details={details} />
	) : (
		<DetailItemLite category={category} details={details} />
	);
};

const DetailItemFull = ({ category, details }: DetailItemProps) => {
	return (
		<>
			<GridColumn xs="12" md="4">
				<TextBody size="md" bold className={Styles.title}>
					{category}
				</TextBody>
			</GridColumn>
			<GridColumn xs="12" md="8">
				<GridRow className={Styles.list}>
					{details.map((detail: string) => {
						return (
							<GridColumn
								xs="6"
								key={`detail_item_${category}_${detail}`}
								className={Styles.itemDetail}
							>
								{detail}
							</GridColumn>
						);
					})}
				</GridRow>
			</GridColumn>
		</>
	);
};

const DetailItemLite = ({ category, details }: DetailItemProps) => {
	const [expanded, setExpanded] = useState(false);
	const readMore = details.length > 6;

	return (
		<>
			<div className={Styles.detailCategory}>{category}</div>
			<ReadMore
				classes={{
					link: Styles.readMoreLink,
					container: Styles.readMoreContainer,
				}}
				expanded={expanded}
				setExpanded={setExpanded}
				showReadMore={readMore}
			>
				<ul className={Styles.list}>
					{details.map((detail: string, index) => {
						if (!expanded && index > 5) {
							return null;
						}

						return <li key={`detail_item_${category}_${detail}`}>{detail}</li>;
					})}
				</ul>
			</ReadMore>
		</>
	);
};
interface DetailsProps {
	vendor: Decorated;
}

const Details = ({ vendor }: DetailsProps) => {
	const isLiteStorefront = useLiteStorefront();

	const flattenedDetails = DetailsHelper.detailsFlattener(vendor) as Record<
		string,
		string[]
	>;

	if (!isLiteStorefront) {
		(flattenedDetails as { 'Business Attributes'?: string })[
			'Business Attributes'
		] = undefined;
	}

	const facetsLength = Object.keys(flattenedDetails).length;

	if (!facetsLength) {
		return null;
	}

	return (
		<>
			{map(flattenedDetails, (details: string[], category) => {
				if (details !== undefined) {
					return !isLiteStorefront ? (
						<Fragment key={`detail_column_${category}`}>
							<GridRow className={Styles.detailSection}>
								<DetailItem category={category} details={details} />
							</GridRow>
							<div className={Styles.divider} />
						</Fragment>
					) : (
						<GridColumn
							xs="12"
							md="6"
							className={Styles.detailWrapper}
							key={`detail_column_${category}`}
						>
							<DetailItem category={category} details={details} />
						</GridColumn>
					);
				}
			})}
		</>
	);
};

interface Props {
	vendor: Decorated;
}

export const Detail: FC<Props> = (props) => {
	const { vendor } = props;
	const isLiteStorefront = useLiteStorefront();
	const isInDetailedPricing = useDetailedPricing();
	const awards = useAtomValue(vendorAwardsAtom);

	const hasDesigners = Boolean(
		vendor.designers?.length && vendor.designers.length > 0,
	);
	const hasAwards = Boolean(awards?.length);
	const awardsTrackingRef = useTrackStorefrontImpression(
		'Awards and Affiliations',
	);

	// Calculate column sizes for lite storefront layout
	const columnSize = hasDesigners && hasAwards ? '6' : '12';

	return !isLiteStorefront ? (
		<>
			{<Details {...props} vendor={vendor} />}
			{hasDesigners && <Designers designers={vendor.designers} {...props} />}
			{hasAwards && (
				<GridRow>
					<Awards fullWidth={true} trackingRef={awardsTrackingRef} />
				</GridRow>
			)}
		</>
	) : (
		<GridRow classes={compose({ row: Styles.detailsRow })}>
			<Details {...props} vendor={vendor} />
			{hasDesigners && (
				<Designers
					designers={vendor.designers}
					columnSize={columnSize}
					{...props}
				/>
			)}
			{hasAwards && !isInDetailedPricing && (
				<Awards columnSize={columnSize} trackingRef={awardsTrackingRef} />
			)}
		</GridRow>
	);
};
